/**
 * Alta y edicion de producto.
 *
 * LA DECISION QUE ORDENA TODO ESTE FORMULARIO
 * -------------------------------------------
 * El precio de venta no es un campo. Es el resultado de cuatro decisiones —
 * costo, markup, moneda de venta y redondeo — que se toman en la seccion
 * «Cómo se forma el precio» y cuyo efecto se ve, en vivo, en la banda que queda
 * fija arriba de los botones. Nadie deberia poder guardar un producto sin haber
 * visto el precio que le puso.
 *
 * TRES COSAS QUE EL BACKEND RECHAZA Y QUE ACA NO SE PUEDEN NI INTENTAR
 * -------------------------------------------------------------------
 *  1. `precio_moneda` sin elegir. La tabla tiene default 'ARS', y ese default
 *     silencioso deja un iPhone cotizado en pesos que nunca se repricia con el
 *     dolar. El selector esta arriba de todo y siempre viaja en el alta.
 *  2. Un markup FIJO sin moneda. Un importe sin unidad no se suma a un costo que
 *     esta en otra: el campo aparece pegado al monto en cuanto se elige FIJO.
 *  3. `stock_actual` editable. Lo mantienen los ingresos y las salidas, que
 *     dejan rastro. En edicion se muestra como dato y con el camino correcto al
 *     lado; nunca como campo. Ese camino es «Corregir contando»: pregunta
 *     cuantas hay y arma el ajuste por la diferencia, que es como se piensa
 *     parado frente al estante.
 *
 * DOS EDITORES, UN SOLO FORMULARIO
 * --------------------------------
 * El alta pregunta las cuatro cosas. La EDICION abre corta —costo, markup,
 * precio y el resultado— porque despues del dia que el producto se crea eso es
 * lo unico que se vuelve a tocar. «Editar todos los datos» destapa la ficha
 * entera sin perder nada: es el mismo `form`, la misma validacion y el mismo
 * guardado. Ver `EditorPrecio`.
 *
 * El paso de revision queda para el ALTA, que es donde se decide lo que despues
 * no se vuelve a mirar. En edicion se guarda derecho: los cambios ya se ven en
 * el panel del precio mientras se escriben, y las advertencias estan a la vista
 * arriba del boton.
 */
import { fichasDeColorDelModelo } from "@/components/iphones/entrada";
import { esIphoneSellado } from "@/components/iphones/logica";
import { memoriaQueSigue } from "@/components/iphones/memorias";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Boxes, ClipboardCheck, History, Info, Trash2 } from "lucide-react";
import { formatear, formatearNumero, type Moneda } from "@tora/shared";

import {
  SelectorDeColor,
  SelectorDeMemoria,
  SelectorDeModelo,
} from "@/components/iphones/FichaDelEquipo";
import { Importe, InputImporte, SelectorMoneda } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Desplegable } from "@/components/ui/desplegable";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Segmented, type OpcionSegmented } from "@/components/ui/segmented";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import {
  aNumero,
  type CambiosProducto,
  type MarkupTipo,
  type NuevoProducto,
  type Producto,
  type Proveedor,
  type Redondeo,
  type Rubro,
} from "@/lib/tipos";

import { Campo, Casilla, CampoCantidad, mostrarCantidad } from "./Campos";
import { nombreCompleto } from "./nombre";
import { EditorPrecio } from "./EditorPrecio";
import { SimuladorPrecio } from "./SimuladorPrecio";
import {
  FORMULARIO_VACIO,
  MARKUPS_UI,
  REDONDEOS_UI,
  aCambiosProducto,
  aNuevoProducto,
  advertenciasProducto,
  ayudaDelPrecioEscrito,
  desdeProducto,
  fijarElPrecio,
  nombreGuardado,
  opcionesDeRedondeo,
  precioEnElCampo,
  precioEscritoA,
  simularPrecio,
  validarProducto,
  type FormularioProducto,
} from "./logica";

export interface DialogProductoProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  /** `null` = alta. */
  producto: Producto | null;
  /**
   * Con que valores arranca un ALTA, segun de donde se la abrio. La solapa
   * «A pedido» manda la casilla marcada, para no hacer elegir dos veces lo
   * que ya se eligio al entrar ahi. El rubro no: es la familia de la cosa.
   *
   * Son valores INICIALES, no imposiciones: se pueden cambiar en el formulario.
   * En edicion se ignora — ahi manda la ficha guardada.
   */
  altaPreset?: Partial<FormularioProducto>;
  rubros: ReadonlyArray<Rubro>;
  /** Las marcas ya usadas en el catalogo. Solo sugieren: el campo es libre. */
  marcasConocidas?: ReadonlyArray<string>;
  proveedores: ReadonlyArray<Proveedor>;
  cotizacion: number;
  guardando?: boolean;
  onCrear: (datos: NuevoProducto) => void;
  onActualizar: (cambios: CambiosProducto) => void;
  /** Manda a /stock para mover unidades. Se ofrece solo en edicion. */
  onIrAStock?: () => void;
  /** Abre el conteo de este producto. El camino corto, y el que casi siempre se usa. */
  onContarStock?: () => void;
  /** Abre la linea de tiempo del producto. Se ofrece solo en edicion. */
  onVerMovimientos?: () => void;
  /**
   * Abre la confirmacion de borrado. Se ofrece solo en edicion.
   *
   * La ficha no borra: avisa que se quiere borrar. Quien decide —y quien se
   * come el "tiene ventas, desactivalo"— es `DialogBorrarProducto`, que lo
   * monta la pantalla.
   */
  onBorrar?: () => void;
  /**
   * El catálogo que tiene la pantalla. Solo para decir el stock de la fila del
   * modelo de un iPhone sellado, que está en cero a propósito: las unidades
   * viven en sus fichas de color (0125). Sin la lista, esa fila no dice stock.
   */
  productos?: ReadonlyArray<Producto>;
}

/**
 * La línea de stock del editor corto, o null si no hay un número honesto.
 *
 * La fila del modelo de un sellado («IPHONE 16 128 GB») decía «Stock: 0 u.»
 * con dos colores en el local: se leía `stock_actual`, que en esa fila es
 * siempre cero. Ahora suma sus colores y lo dice así.
 */
function lineaDeStock(
  producto: Producto,
  productos: ReadonlyArray<Producto> | undefined,
): string | null {
  if (esIphoneSellado(producto) && !producto.color) {
    const colores = fichasDeColorDelModelo(producto, productos ?? []);
    if (colores.length === 0) return null;
    const total = colores.reduce(
      (t, p) => t + aNumero(p.stock_actual),
      aNumero(producto.stock_actual),
    );
    return `Stock en sus colores: ${mostrarCantidad(total)} u.`;
  }
  return `Stock: ${mostrarCantidad(aNumero(producto.stock_actual))} u.`;
}

const OPCIONES_MARKUP: ReadonlyArray<OpcionSegmented<MarkupTipo>> = MARKUPS_UI.map((m) => ({
  valor: m.tipo,
  etiqueta: m.label,
}));

export function DialogProducto({
  abierto,
  onAbiertoChange,
  producto,
  altaPreset,
  rubros,
  marcasConocidas = [],
  proveedores,
  cotizacion,
  guardando = false,
  onCrear,
  onActualizar,
  onIrAStock,
  onContarStock,
  onVerMovimientos,
  onBorrar,
  productos,
}: DialogProductoProps): JSX.Element {
  const [paso, setPaso] = useState<"carga" | "revision">("carga");
  /**
   * Con que editor se abre. La edicion arranca en el corto —precio y nada
   * mas— y el alta en la ficha entera, que es cuando hacen falta las cuatro
   * secciones. Es una vista del MISMO formulario: pasar de una a la otra no
   * pierde lo escrito ni cambia lo que se guarda.
   */
  const [vista, setVista] = useState<"precio" | "completo">("completo");
  const [form, setForm] = useState<FormularioProducto>(FORMULARIO_VACIO);
  /**
   * Si ya se intento avanzar. Un alta en blanco no tiene nombre, y avisarlo en
   * rojo antes de que se escriba nada es retar por lo que todavia no se hizo:
   * el aviso aparece al tocar «Revisar», como en el alta guiada.
   */
  const [intento, setIntento] = useState(false);

  const esEdicion = producto !== null;

  // Al abrir se carga el producto o se arranca limpio. Un formulario que
  // recuerda el producto anterior termina editando el equivocado.
  useEffect(() => {
    if (!abierto) return;
    setPaso("carga");
    setIntento(false);
    setVista(producto ? "precio" : "completo");
    setForm(producto ? desdeProducto(producto) : { ...FORMULARIO_VACIO, ...altaPreset });
  }, [abierto, producto, altaPreset]);

  function cambiar<K extends keyof FormularioProducto>(
    campo: K,
    valor: FormularioProducto[K],
  ): void {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  /** Varios campos de una. Lo piden las reglas que tocan mas de uno a la vez. */
  function parchear(patch: Partial<FormularioProducto>): void {
    setForm((f) => ({ ...f, ...patch }));
  }

  const simulacion = useMemo(() => simularPrecio(form, cotizacion), [form, cotizacion]);
  const errores = useMemo(
    () => validarProducto(form, aNumero(producto?.stock_actual)),
    [form, producto],
  );
  const avisos = useMemo(() => advertenciasProducto(form), [form]);

  const cambios = useMemo(
    () => (producto ? aCambiosProducto(form, producto) : null),
    [form, producto],
  );
  const sinCambios = cambios !== null && Object.keys(cambios).length === 0;

  function confirmar(): void {
    if (producto && cambios) onActualizar(cambios);
    else onCrear(aNuevoProducto(form));
  }

  const stockParaConsultar = producto && !form.esUsado ? lineaDeStock(producto, productos) : null;

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      {/* 768px es el techo de un dialogo: mas que eso es una ruta. Las cuatro
          secciones van apiladas y el precio queda fijo abajo. El editor corto
          entra en 512: tres campos no necesitan el ancho de una ficha. */}
      <DialogContent
        tamano={vista === "precio" ? "md" : "lg"}
        /* El editor corto es tambien lo que abre ⌘K para CONSULTAR un producto.
           Con el foco en el costo, un número tipeado de más caía ahí; se abre
           sin foco en ningún campo y se entra al que se quiera cambiar. */
        onOpenAutoFocus={vista === "precio" ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle>
            {paso === "revision"
              ? "Revisá antes de guardar"
              : !esEdicion
                ? "Producto nuevo"
                : // En el editor corto el nombre alcanza: la descripcion de abajo
                  // ya dice que se viene a cambiar, y son nombres largos.
                  vista === "precio"
                  ? nombreCompleto(producto)
                  : `Editar ${nombreCompleto(producto)}`}
            {/* Un usado que se edita aca es siempre un precio de referencia: los
                equipos van por su asistente. Lo dice el titulo, no una nota. */}
            {form.esUsado && paso !== "revision" ? " · precio de referencia" : null}
          </DialogTitle>
          <DialogDescription>
            {paso === "revision"
              ? "Así queda el producto. El precio de venta lo escribe la base con estos datos."
              : vista === "precio"
                ? `Cambiá el costo o el precio. El resto de la ficha queda como está.${
                    // Desde ⌘K se viene a consultar: sin el stock, había que
                    // abrir otra pantalla para saber si había.
                    // En la fila del modelo de un sellado, lo de sus colores.
                    stockParaConsultar ? ` ${stockParaConsultar}` : ""
                  }`
                : "El precio de venta no se escribe: sale del costo, el markup y el redondeo. Se arma abajo mientras cargás."}
          </DialogDescription>
        </DialogHeader>

        {/*
            EL FORMULARIO APILADO, EL PRECIO FIJO ABAJO.

            El encabezado promete que el precio se ve mientras se carga. Para
            cumplirlo, la banda con la cuenta no va dentro del cuerpo que
            scrollea: va entre el cuerpo y los botones, fuera del scroll, como
            el "que queda despues" del cobro. Asi el costo puede estar arriba
            del pliegue o abajo, y el precio se ve igual.
        */}
        <DialogBody>
          {form.esUsado && paso === "carga" ? <AvisoPrecioDeReferencia /> : null}
          {paso === "carga" && vista === "precio" ? (
            <EditorPrecio
              form={form}
              cambiar={cambiar}
              parchear={parchear}
              simulacion={simulacion}
              cotizacion={cotizacion}
              onEditarTodo={() => setVista("completo")}
            />
          ) : paso === "carga" ? (
            <>
              <Identificacion
                form={form}
                cambiar={cambiar}
                parchear={parchear}
                esEdicion={esEdicion}
                rubros={rubros}
                marcasConocidas={marcasConocidas}
                proveedores={proveedores}
              />
              <MotorDePrecio
                form={form}
                cambiar={cambiar}
                parchear={parchear}
                simulacion={simulacion}
                cotizacion={cotizacion}
                esEdicion={esEdicion}
              />
              {/* Un precio de referencia no tiene stock: nada que reponer ni que
                  contar. Contarlo fue como entraron teléfonos sin ficha. */}
              {form.esUsado ? null : (
                <Inventario
                  form={form}
                  cambiar={cambiar}
                  producto={producto}
                  onIrAStock={onIrAStock}
                  onContarStock={onContarStock}
                  onVerMovimientos={onVerMovimientos}
                />
              )}
              <Publicacion form={form} cambiar={cambiar} />
            </>
          ) : (
            <Revision
              form={form}
              simulacion={simulacion}
              cotizacion={cotizacion}
              avisos={avisos}
              rubros={rubros}
              proveedores={proveedores}
              esEdicion={esEdicion}
              sinCambios={sinCambios}
            />
          )}
        </DialogBody>

        {paso === "carga" && vista === "completo" ? (
          <SimuladorPrecio form={form} simulacion={simulacion} cotizacion={cotizacion} banda />
        ) : null}

        {/* Error de formulario: arriba del footer, con la primaria deshabilitada. */}
        {errores.length > 0 && paso === "carga" && (esEdicion || intento) ? (
          <Aviso tono="destructive">
            <ul className="space-y-1">
              {errores.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </Aviso>
        ) : null}

        {/*
            Las advertencias, en edicion, van ACA.

            En el alta las muestra la pantalla de revision. La edicion guarda
            derecho, asi que si no estuvieran a la vista arriba del boton no las
            veria nadie — y son justo las que avisan que el producto se va a
            vender al costo o que quedo publicado pero dado de baja.
        */}
        {esEdicion && paso === "carga" && errores.length === 0 && avisos.length > 0 ? (
          <Aviso tono="warning">
            <ul className="space-y-1">
              {avisos.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </Aviso>
        ) : null}

        {/* En el celular el pie sube a la barra de arriba, y ahí «Borrar»
            caía donde en los demás cuadros va Cancelar. Va al final, solo. */}
        {esEdicion && onBorrar && paso === "carga" ? (
          <div className="sm:hidden">
            <Button type="button" variant="ghost-destructive" onClick={onBorrar}>
              <Trash2 aria-hidden="true" />
              Borrar producto
            </Button>
          </div>
        ) : null}

        <DialogFooter>
          {/*
              Borrar vive con la ficha porque es una operacion sobre el
              PRODUCTO. Va pegado a la izquierda y separado de las otras dos:
              es destructivo y no tiene que caer bajo el pulgar que venia a
              guardar. Solo en edicion —no se borra lo que todavia no existe— y
              solo en el paso de carga, porque la revision es del alta.
          */}
          {esEdicion && onBorrar && paso === "carga" ? (
            <Button
              type="button"
              variant="ghost-destructive"
              className="max-sm:hidden sm:mr-auto"
              onClick={onBorrar}
            >
              <Trash2 aria-hidden="true" />
              Borrar producto
            </Button>
          ) : null}
          {paso === "carga" ? (
            <>
              <Button type="button" variant="ghost" onClick={() => onAbiertoChange(false)}>
                Cancelar
              </Button>
              {/*
                  En edicion se guarda desde aca. El paso de revision existe para
                  el alta, que decide de cero; cambiar un costo es una cuenta que
                  ya se esta viendo hecha en el panel de al lado, y hacerla
                  confirmar dos veces no la hace mas segura.
              */}
              {esEdicion ? (
                <Button
                  type="button"
                  cargando={guardando}
                  disabled={errores.length > 0 || sinCambios}
                  onClick={confirmar}
                >
                  Guardar cambios
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => {
                    if (errores.length > 0) setIntento(true);
                    else setPaso("revision");
                  }}
                >
                  Revisar
                </Button>
              )}
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => setPaso("carga")}>
                <ArrowLeft aria-hidden="true" />
                Volver
              </Button>
              <Button type="button" cargando={guardando} disabled={sinCambios} onClick={confirmar}>
                {esEdicion ? "Guardar cambios" : "Crear producto"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type Cambiar = <K extends keyof FormularioProducto>(campo: K, valor: FormularioProducto[K]) => void;

// ─────────────────────────────────────────────────────────────────────────────

function Identificacion({
  form,
  cambiar,
  parchear,
  esEdicion,
  rubros,
  marcasConocidas,
  proveedores,
}: {
  form: FormularioProducto;
  cambiar: Cambiar;
  parchear: (patch: Partial<FormularioProducto>) => void;
  esEdicion: boolean;
  rubros: ReadonlyArray<Rubro>;
  marcasConocidas: ReadonlyArray<string>;
  proveedores: ReadonlyArray<Proveedor>;
}): JSX.Element {
  return (
    // Mismo marco y mismo titulo que las otras tres secciones. Era la unica
    // suelta, y al lado de tres tarjetas con borde se leia como si los campos
    // de arriba no pertenecieran a ningun lado.
    <Card as="section" padding="normal" className="space-y-4">
      <TituloSeccion as="h3">Qué es</TituloSeccion>

      {/*
          UN TELEFONO NO SE NOMBRA: SE DESCRIBE.

          El nombre de un equipo sale del modelo y de la memoria, asi que el
          campo de texto no aparece: lo que aparece son las preguntas de las que
          el nombre sale, y debajo el nombre que va a quedar.

          Escribirlo a mano era lo que dejaba "16 pro max", "16 PROMAX" y
          "16 Pro Max" como tres productos distintos, y ademas era el unico
          lugar donde se le podia poner el color a un sellado: adentro del
          nombre, donde ningun filtro y ningun buscador lo pueden leer. El color
          ahora se elige de los que ese modelo tiene y va a su columna.

          El resto del catalogo —un cable, un perfume— sigue con el campo: no
          hay ficha de la que derivarle un nombre.
      */}
      {form.esEquipo ? (
        <div className="space-y-4">
          <SelectorDeModelo
            modelo={form.modelo}
            // Cambiar de modelo borra el color: el "Desert Titanium" del 16 Pro
            // no existe en el 17, y un color que el modelo no tiene es un dato
            // falso.
            onChange={(modelo) =>
              parchear({ modelo, color: "", memoriaGb: memoriaQueSigue(modelo, form.memoriaGb) })
            }
            // Acá también se editan los precios de referencia de usados, que
            // pueden ser de otra marca.
            permiteOtro
          />
          <SelectorDeMemoria
            modelo={form.modelo}
            memoriaGb={form.memoriaGb}
            onChange={(memoriaGb) => cambiar("memoriaGb", memoriaGb)}
          />
          {/* Un precio de referencia no tiene color: es de todos los equipos
              de ese modelo, y la base rechaza el que se le ponga. */}
          {form.esUsado ? null : (
            <SelectorDeColor
              modelo={form.modelo}
              color={form.color}
              onChange={(color) => cambiar("color", color)}
              // Un sellado sin color es la fila de lista de precios del
              // modelo, que viene en todos: ahi «Todos» es una respuesta.
              admiteTodos
              permiteEscribir
              ayuda="«Todos» si se consigue en cualquiera de los del modelo."
            />
          )}
          <Card variant="muted" padding="compacto">
            <p className="text-xs text-muted-foreground">Se va a llamar</p>
            <p className="text-sm font-medium">{nombreGuardado(form) || "—"}</p>
          </Card>
        </div>
      ) : (
        <Campo label="Nombre" id="prod-nombre" requerido>
          <Input
            id="prod-nombre"
            value={form.nombre}
            onChange={(e) => cambiar("nombre", e.target.value)}
            placeholder="CABLE MALLADO C A C"
            autoComplete="off"
          />
        </Campo>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Campo label="Marca" id="prod-marca" ayuda="De quién es. Podés dejarlo vacío.">
          <Input
            id="prod-marca"
            value={form.marca}
            onChange={(e) => cambiar("marca", e.target.value)}
            placeholder="Samsung, Lattafa, JBL…"
            autoComplete="off"
            list="marcas-conocidas"
          />
          {/* Sugiere las que ya existen sin obligar: la marca es texto libre y
              tiene que poder entrar una nueva sin pasar por un ABM. */}
          <datalist id="marcas-conocidas">
            {marcasConocidas.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </Campo>

        <Campo label="Familia" id="prod-rubro">
          <Select
            value={form.rubroId}
            onValueChange={(v) => cambiar("rubroId", v === "_" ? "" : v)}
          >
            <SelectTrigger id="prod-rubro" aria-label="Familia">
              <SelectValue placeholder="Sin familia" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_">Sin familia</SelectItem>
              {rubros.map((r) => (
                <SelectItem key={r.id} value={String(r.id)}>
                  {r.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>

        <Campo label="Proveedor" id="prod-proveedor">
          <Select
            value={form.proveedorId}
            onValueChange={(v) => cambiar("proveedorId", v === "_" ? "" : v)}
          >
            <SelectTrigger id="prod-proveedor" aria-label="Proveedor">
              <SelectValue placeholder="Sin proveedor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_">Sin proveedor</SelectItem>
              {proveedores.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>
                  {p.nombre} · lista en {p.moneda_lista}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>
      </div>

      {/*
          LO QUE CASI NUNCA SE TOCA, PLEGADO.

          Los dos codigos y la descripcion son tres campos que en un alta se
          dejan vacios nueve de cada diez veces: el interno lo pone la base, el
          del proveedor solo sirve si se va a machear su lista, y la
          descripcion es del catalogo publico. Arriba de la seccion quedaban
          entre el nombre y la familia, que son las dos que si se contestan
          siempre. Plegado no desaparece: se guarda igual, y se abre solo si
          venia con algo cargado.
      */}
      <Desplegable
        resumen="Códigos y descripción"
        abiertoInicial={
          form.codigoInterno.trim() !== "" ||
          form.codigoExterno.trim() !== "" ||
          form.descripcion.trim() !== ""
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo
            label="Código interno"
            id="prod-codigo"
            // Sin ayuda: el placeholder ya dice "Automático", y en edicion el
            // campo esta bloqueado. Decirlo tres veces no lo hace mas claro.
          >
            <Input
              id="prod-codigo"
              value={form.codigoInterno}
              disabled={esEdicion}
              onChange={(e) => cambiar("codigoInterno", e.target.value)}
              placeholder="Automático"
              autoComplete="off"
              className="tabular-nums"
            />
          </Campo>

          <Campo
            label="Código del proveedor"
            id="prod-codigo-ext"
            ayuda="Con esto se machea su lista de precios."
          >
            <Input
              id="prod-codigo-ext"
              value={form.codigoExterno}
              onChange={(e) => cambiar("codigoExterno", e.target.value)}
              placeholder="APL-IP15-128"
              autoComplete="off"
            />
          </Campo>
        </div>

        <Campo label="Descripción" id="prod-descripcion">
          <Input
            id="prod-descripcion"
            value={form.descripcion}
            onChange={(e) => cambiar("descripcion", e.target.value)}
            placeholder="Detalle opcional para el catálogo"
            autoComplete="off"
          />
        </Campo>
      </Desplegable>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function MotorDePrecio({
  form,
  cambiar,
  parchear,
  simulacion,
  cotizacion,
  esEdicion,
}: {
  form: FormularioProducto;
  cambiar: Cambiar;
  parchear: (patch: Partial<FormularioProducto>) => void;
  simulacion: ReturnType<typeof simularPrecio>;
  /** Para que los selectores de moneda conviertan el numero, y no solo lo etiqueten. */
  cotizacion: number;
  /**
   * En el alta la moneda de venta va al frente; en la edicion, plegada. Ver el
   * comentario de `<MonedaDeVenta>`.
   */
  esEdicion: boolean;
}): JSX.Element {
  /*
    Cada redondeo, con el precio que da, DENTRO del selector.

    Antes esto era una fila de chips debajo del campo: las mismas cinco
    opciones una segunda vez, con el precio al lado. Eran dos controles para
    una sola decision. Ahora el precio viaja en la opcion, que es donde se la
    elige, y sigue estando a la vista que ningun redondeo baja el precio.
  */
  const precioPorRedondeo = new Map(
    (simulacion.calculable ? opcionesDeRedondeo(simulacion.precioBruto) : []).map((o) => [
      o.modo,
      o.precio,
    ]),
  );

  return (
    <Card as="section" padding="normal" className="space-y-4">
      <TituloSeccion as="h3">Cómo se forma el precio</TituloSeccion>
      {/*
        El porque de la seccion, UNA vez. Antes estaba repartido en tres ayudas
        de campo que repetian la misma regla desde angulos distintos.
      */}
      <p className="text-xs leading-relaxed text-muted-foreground">
        El costo más el markup dan el precio, y el redondeo siempre va para arriba: nunca se come
        margen.
      </p>

      {esEdicion ? null : <MonedaDeVenta form={form} cambiar={cambiar} />}

      <InputImporte
        label="Costo de reposición"
        valor={form.costo}
        onValorChange={(v) => cambiar("costo", v)}
        moneda={form.costoMoneda}
        onMonedaChange={(m) => cambiar("costoMoneda", m)}
        cotizacion={cotizacion}
      />

      <div className="space-y-2">
        <span className="block text-sm font-medium leading-none">Markup</span>
        <Segmented
          etiqueta="Tipo de markup"
          // Del alto de los campos del formulario, como el porcentaje de abajo.
          tamano="md"
          opciones={OPCIONES_MARKUP}
          valor={form.markupTipo}
          onChange={(v) => cambiar("markupTipo", v)}
        />
        <p className="text-xs text-muted-foreground">
          {MARKUPS_UI.find((m) => m.tipo === form.markupTipo)?.ayuda}
        </p>

        {form.markupTipo === "PORCENTAJE" ? (
          <CampoCantidad
            label="Porcentaje sobre el costo"
            valor={form.markupValor}
            onValorChange={(v) => cambiar("markupValor", v)}
            sufijo="%"
            requerido
          />
        ) : (
          <InputImporte
            label="Monto fijo sobre el costo"
            valor={form.markupValor}
            onValorChange={(v) => cambiar("markupValor", v)}
            moneda={form.markupMoneda}
            onMonedaChange={(m) => cambiar("markupMoneda", m)}
            cotizacion={cotizacion}
            requerido
            ayuda="Un markup fijo es un importe: necesita su moneda para poder sumarse al costo."
          />
        )}
      </div>

      {/* ── Escribir el precio ──
          Dos cosas que antes eran una sola. Escribir el precio a mano era
          tambien congelarlo, y eso rompia el caso mas comun al mudarse: se sabe
          a cuanto se vende, no cuanto se gana. Ahora escribirlo DESPEJA el
          margen —el producto sigue vivo— y congelarlo es una decision aparte,
          para cuando el precio no se tiene que mover por una razon comercial. */}
      <div className="space-y-2 border-t border-border pt-3">
        <InputImporte
          label="O escribí el precio de venta"
          valor={precioEnElCampo(form, simulacion)}
          onValorChange={(v) => parchear(precioEscritoA(form, v, cotizacion))}
          // Sin `onMonedaChange`: el precio va SI o SI en la moneda de venta del
          // producto. Un precio en otra moneda no significa nada.
          moneda={form.precioMoneda}
          ayuda={ayudaDelPrecioEscrito(form, simulacion)}
        />

        <Casilla
          label="Fijar este precio"
          ayuda="No cambia cuando cambia el costo o el dólar. Para un precio que no se tiene que mover."
          marcado={form.usaPrecioManual}
          onMarcadoChange={(v) => parchear(fijarElPrecio(form, v, cotizacion))}
        />
      </div>

      {/*
          Redondeo y —en edicion— moneda de venta: lo que se elige una vez.

          Las dos son decisiones del dia que el producto se crea y despues
          quedan quietas por años. Arriba, entre el costo y el markup, cobraban
          el mismo peso visual que los dos numeros que si se retocan seguido.
          Plegadas siguen a un click y el resumen dice como estan, que es lo
          unico que se necesita saber sin abrirlas.
      */}
      <Desplegable
        resumen={
          <span className="min-w-0 truncate">
            {esEdicion
              ? `Moneda y redondeo · ${form.precioMoneda} · ${REDONDEOS_UI.find((r) => r.modo === form.redondeo)?.label.toLowerCase()}`
              : `Redondeo comercial · ${REDONDEOS_UI.find((r) => r.modo === form.redondeo)?.label.toLowerCase()}`}
          </span>
        }
      >
        {esEdicion ? <MonedaDeVenta form={form} cambiar={cambiar} /> : null}

        <div className="space-y-1.5">
          <Label htmlFor="prod-redondeo">Redondeo comercial</Label>
          <Select value={form.redondeo} onValueChange={(v) => cambiar("redondeo", v as Redondeo)}>
            <SelectTrigger id="prod-redondeo" aria-label="Redondeo comercial">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REDONDEOS_UI.map((r) => {
                const precio = precioPorRedondeo.get(r.modo);
                return (
                  <SelectItem key={r.modo} value={r.modo}>
                    {precio === undefined
                      ? r.label
                      : `${r.label}: ${formatear(precio, form.precioMoneda)}`}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {REDONDEOS_UI.find((r) => r.modo === form.redondeo)?.ayuda}
          </p>
        </div>
      </Desplegable>
    </Card>
  );
}

/**
 * La moneda de venta.
 *
 * Es la decision que determina si el producto se repricia cuando se mueve el
 * dolar, y la que el default de la tabla ('ARS') resuelve mal en silencio. Por
 * eso en el ALTA va primera y a la vista.
 *
 * En la EDICION esa razon ya no corre: la moneda esta elegida, se ve en el
 * panel del precio y cambiarla es rehacer como se cotiza el producto, no
 * corregirlo. Ahi va adentro del desplegable, con el resumen diciendo cual es.
 *
 * El rotulo es un span y no una Label: el control ya tiene su etiqueta
 * accesible y no hay un id al que apuntar.
 */
function MonedaDeVenta({
  form,
  cambiar,
}: {
  form: FormularioProducto;
  cambiar: Cambiar;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="min-w-0 space-y-1">
        <span className="block text-sm font-medium leading-none">En qué moneda se cotiza</span>
        <p className="text-xs text-muted-foreground">
          {form.precioMoneda === "ARS"
            ? "El precio de lista queda en pesos. Si el costo está en dólares, se repricia solo."
            : "El precio de lista queda en dólares y se convierte a pesos al vender."}
        </p>
      </div>
      <SelectorMoneda
        valor={form.precioMoneda}
        onChange={(m: Moneda) => cambiar("precioMoneda", m)}
        etiqueta="Moneda de venta"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lo que es un precio de referencia, dicho arriba de todo.
 *
 * Sin esto la ficha parecía la de un equipo con datos por completar: se le
 * contaba stock, se le elegía color, y en septiembre entraron así cuatro
 * teléfonos del local que nunca tuvieron su ficha.
 */
function AvisoPrecioDeReferencia(): JSX.Element {
  return (
    <Aviso tono="info">
      <div className="space-y-1">
        <p className="font-medium">Es solo un precio de referencia.</p>
        <p>
          Dice cuánto vale este modelo usado y se ofrece a pedido. No es un teléfono: no lleva
          batería, IMEI, color ni stock. Un equipo que entra se carga desde Inventario (Ingreso,
          «iPhone usado»).
        </p>
      </div>
    </Aviso>
  );
}

function Inventario({
  form,
  cambiar,
  producto,
  onIrAStock,
  onContarStock,
  onVerMovimientos,
}: {
  form: FormularioProducto;
  cambiar: Cambiar;
  producto: Producto | null;
  onIrAStock?: () => void;
  onContarStock?: () => void;
  onVerMovimientos?: () => void;
}): JSX.Element {
  return (
    <Card as="section" padding="normal" className="space-y-4">
      <TituloSeccion as="h3">Reposición</TituloSeccion>

      {/*
          Los umbrales van plegados en el alta y abiertos cuando ya tienen
          valor. Nadie sabe el minimo de un producto el dia que lo carga: se
          aprende despues de verlo vender un par de meses, y preguntarlo en el
          alta solo consigue dos ceros que apagan la Reposicion entera.
          `forzarAbierto` los destapa si la validacion los rechaza.
      */}
      <Desplegable
        resumen="Umbrales de reposición"
        abiertoInicial={form.stockMinimo !== null || form.stockIdeal !== null}
        forzarAbierto={
          (form.stockMinimo !== null && form.stockMinimo < 0) ||
          (form.stockIdeal !== null && form.stockIdeal < 0)
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CampoCantidad
            label="Stock mínimo"
            valor={form.stockMinimo}
            onValorChange={(v) => cambiar("stockMinimo", v)}
            sufijo="u."
            ayuda="Debajo de esto el producto entra en crítico."
          />
          <CampoCantidad
            label="Stock ideal"
            valor={form.stockIdeal}
            onValorChange={(v) => cambiar("stockIdeal", v)}
            sufijo="u."
            ayuda="Hasta acá sugiere comprar la reposición."
          />
        </div>
      </Desplegable>

      {/*
        El stock actual se MUESTRA y no se edita. El backend rechaza el PATCH que
        lo traiga, y con razon: si se pudiera escribir a mano, la diferencia
        entre lo que decia el sistema y lo que hay en el estante desapareceria
        sin dejar rastro de quien la hizo ni por que.
      */}
      {producto ? (
        <Card
          variant="muted"
          padding="compacto"
          className="flex flex-wrap items-center justify-between gap-3"
        >
          <div className="min-w-0">
            <TituloSeccion nivel="etiqueta" as="span">
              Stock actual
            </TituloSeccion>
            {/* Unidades, no plata: sin chip, sin color de moneda. */}
            <p className="text-lg font-semibold tabular-nums">
              {mostrarCantidad(aNumero(producto.stock_actual))} u.
            </p>
            <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
              <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              <span>
                Acá no se edita a mano. Si contaste y da otra cosa, usá «Corregir contando»: ponés
                cuántas hay y el sistema hace el ajuste, con quién, cuándo y por qué.
              </span>
            </p>
          </div>
          {/* Sin `shrink-0`: en el celular «Mover stock» quedaba afuera por la
              derecha porque el grupo no podía bajar de renglón. */}
          <div className="flex min-w-0 flex-wrap gap-2">
            {onContarStock ? (
              <Button type="button" variant="outline" size="sm" onClick={onContarStock}>
                <ClipboardCheck aria-hidden="true" />
                Corregir contando
              </Button>
            ) : null}
            {onVerMovimientos ? (
              <Button type="button" variant="ghost" size="sm" onClick={onVerMovimientos}>
                <History aria-hidden="true" />
                Ver movimientos
              </Button>
            ) : null}
            {onIrAStock ? (
              <Button type="button" variant="ghost" size="sm" onClick={onIrAStock}>
                <Boxes aria-hidden="true" />
                Mover stock
              </Button>
            ) : null}
          </div>
        </Card>
      ) : null}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Publicacion({
  form,
  cambiar,
}: {
  form: FormularioProducto;
  cambiar: Cambiar;
}): JSX.Element {
  return (
    <Card as="section" padding="normal" className="space-y-2">
      {/* Sin «Publicado»: que se ve en la tienda se decide en Tienda online,
          y solo ahi. Dos lugares para lo mismo terminaban diciendo cosas
          distintas. */}
      <TituloSeccion as="h3">Estado</TituloSeccion>
      <Casilla
        label="A pedido"
        ayuda="Sin stock se muestra como «a pedido» en vez de «sin stock»."
        marcado={form.aPedido}
        onMarcadoChange={(v) => cambiar("aPedido", v)}
      />
      <Casilla
        label="Activo"
        ayuda="Dado de baja no se puede cargar en una venta ni aparece en las listas."
        marcado={form.activo}
        onMarcadoChange={(v) => cambiar("activo", v)}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Revision({
  form,
  simulacion,
  cotizacion,
  avisos,
  rubros,
  proveedores,
  esEdicion,
  sinCambios,
}: {
  form: FormularioProducto;
  simulacion: ReturnType<typeof simularPrecio>;
  cotizacion: number;
  avisos: string[];
  rubros: ReadonlyArray<Rubro>;
  proveedores: ReadonlyArray<Proveedor>;
  esEdicion: boolean;
  sinCambios: boolean;
}): JSX.Element {
  const rubro = rubros.find((r) => String(r.id) === form.rubroId);
  const proveedor = proveedores.find((p) => String(p.id) === form.proveedorId);

  return (
    <>
      <SimuladorPrecio form={form} simulacion={simulacion} cotizacion={cotizacion} />

      <Card variant="muted" padding="normal">
        <dl className="space-y-2 text-sm">
          {/* El nombre del equipo es el ARMADO, no lo que haya quedado escrito
              en la ficha vieja: es lo que se va a guardar. */}
          <FilaResumen termino="Nombre" valor={nombreGuardado(form)} />
          {form.esEquipo ? (
            <FilaResumen termino="Color" valor={form.color.trim() || "Todos los del modelo"} />
          ) : null}
          <FilaResumen termino="Familia" valor={rubro?.nombre ?? "Sin familia"} />
          <FilaResumen termino="Marca" valor={form.marca.trim() || "Sin marca"} />
          <FilaResumen termino="Proveedor" valor={proveedor?.nombre ?? "Sin proveedor"} />
          <FilaResumen
            termino="Costo"
            valor={<Importe monto={form.costo ?? 0} moneda={form.costoMoneda} />}
          />
          <FilaResumen
            termino="Markup"
            valor={
              form.markupTipo === "PORCENTAJE" ? (
                <span className="tabular-nums">
                  {formatearNumero(form.markupValor ?? 0)} % sobre el costo
                </span>
              ) : (
                <>
                  <Importe monto={form.markupValor ?? 0} moneda={form.markupMoneda} /> fijos
                </>
              )
            }
          />
          <FilaResumen
            termino="Reposición"
            valor={
              <span className="tabular-nums">
                mínimo {mostrarCantidad(form.stockMinimo ?? 0)} · ideal{" "}
                {mostrarCantidad(form.stockIdeal ?? 0)}
              </span>
            }
          />
          <FilaResumen
            termino="Estado"
            valor={[form.activo ? "activo" : "dado de baja", form.aPedido ? "a pedido" : null]
              .filter(Boolean)
              .join(" · ")}
          />
        </dl>
      </Card>

      {avisos.length > 0 ? (
        <Aviso tono="warning">
          <ul className="space-y-1">
            {avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </Aviso>
      ) : null}

      {esEdicion && sinCambios ? (
        <Aviso tono="neutral" compacto>
          No cambiaste nada todavía.
        </Aviso>
      ) : null}
    </>
  );
}

function FilaResumen({ termino, valor }: { termino: string; valor: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0">
        <TituloSeccion nivel="etiqueta" as="span">
          {termino}
        </TituloSeccion>
      </dt>
      <dd className="min-w-0 text-right">{valor}</dd>
    </div>
  );
}
